import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react';
import type { AppState, Notificaciones, Perfil, Preferencias } from '../types';
import { Field, PageShell, Toggle, glassLight, inputStyle, T, primaryButton } from '../ui';
function PanelLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }} data-id="element-360" data-mp="2tqshgf0ww4f7">
      
      <span style={{
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: T.accent,
      boxShadow: `0 0 8px ${T.accent}`
    }} data-id="element-361" data-mp="33q1b16rv7h11" />
      
      {children}
    </div>;
}
const panelVariant = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.34,
      ease: [0.2, 0.8, 0.2, 1]
    }
  }
};
type Tab = 'general' | 'usuarios' | 'roles';
export function Configuracion({
  state,
  setPerfil,
  setNotif,
  setPrefs,
  usuariosNode,
  rolesNode,
  cambiarPassword
}: {
  state: AppState;
  setPerfil: (value: Perfil) => void;
  setNotif: (value: Notificaciones) => void;
  setPrefs: (value: Preferencias) => void;
  usuariosNode?: React.ReactNode;
  rolesNode?: React.ReactNode;
  cambiarPassword?: (actual: string, nueva: string) => Promise<{
    ok: boolean;
    error?: string;
  }>;
}) {
  // ── Cambio de contraseña ──────────────────────────────────────────────
  // Va aparte del resto del formulario y con su propio botón: no es lo mismo
  // corregir el nombre del perfil que cambiar la llave de entrada al sistema,
  // y mezclarlos hace fácil cambiarla sin querer.
  //
  // Quien verifica la contraseña actual es el servidor (ver `cambiarPassword`
  // en db.ts): la real está cifrada en la base y nunca sale de ahí, así que
  // no hay forma de compararla aquí.
  const MIN_PASS = 6;
  const [passActual, setPassActual] = useState('');
  const [passNueva, setPassNueva] = useState('');
  const [passConfirmar, setPassConfirmar] = useState('');
  const [showPassActual, setShowPassActual] = useState(false);
  const [showPassNueva, setShowPassNueva] = useState(false);
  const [passError, setPassError] = useState('');
  const [passOk, setPassOk] = useState(false);
  const [cambiandoPass, setCambiandoPass] = useState(false);

  // Al escribir se borran los avisos: si no, el error de un intento anterior
  // sigue en pantalla mientras ya se está corrigiendo.
  const alEscribirPass = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPassError('');
    setPassOk(false);
  };
  const handleCambiarPassword = async () => {
    if (!cambiarPassword || cambiandoPass) return;
    setPassError('');
    setPassOk(false);
    if (passNueva.length < MIN_PASS) {
      setPassError(`La nueva contraseña debe tener al menos ${MIN_PASS} caracteres.`);
      return;
    }
    if (passNueva !== passConfirmar) {
      setPassError('La confirmación no coincide con la nueva contraseña.');
      return;
    }
    if (passNueva === passActual) {
      setPassError('La nueva contraseña es igual a la actual.');
      return;
    }
    setCambiandoPass(true);
    const {
      ok,
      error
    } = await cambiarPassword(passActual, passNueva);
    setCambiandoPass(false);
    if (!ok) {
      setPassError(error ?? 'No se pudo cambiar la contraseña.');
      return;
    }
    // Se limpian los tres campos: dejar la contraseña nueva escrita en
    // pantalla no aporta nada y sí puede leerla alguien por encima del hombro.
    setPassActual('');
    setPassNueva('');
    setPassConfirmar('');
    setShowPassActual(false);
    setShowPassNueva(false);
    setPassOk(true);
  };
  // ── Draft state local (no guarda hasta presionar "Guardar") ──────────────
  const [draftPerfil, setDraftPerfil] = useState<Perfil>({
    ...state.perfil
  });
  const [draftNotif, setDraftNotif] = useState<Notificaciones>({
    ...state.notif
  });
  const [draftPrefs, setDraftPrefs] = useState<Preferencias>({
    ...state.prefs
  });
  // Cuando cambia el usuario logueado (session), sincronizamos el draft
  useEffect(() => {
    setDraftPerfil({
      ...state.perfil
    });
    setDraftNotif({
      ...state.notif
    });
    setDraftPrefs({
      ...state.prefs
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.perfil.correo]);
  // ── Feedback de guardado ─────────────────────────────────────────────────
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Detectar si hay cambios pendientes
  const hasChanges = JSON.stringify(draftPerfil) !== JSON.stringify(state.perfil) || JSON.stringify(draftNotif) !== JSON.stringify(state.notif) || JSON.stringify(draftPrefs) !== JSON.stringify(state.prefs);
  const handleSave = () => {
    setSaving(true);
    setPerfil(draftPerfil);
    setNotif(draftNotif);
    setPrefs(draftPrefs);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 350);
  };
  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs: {
    id: Tab;
    label: string;
    show: boolean;
  }[] = [{
    id: 'general',
    label: 'General',
    show: true
  }, {
    id: 'usuarios',
    label: 'Usuarios',
    show: !!usuariosNode
  }, {
    id: 'roles',
    label: 'Roles y permisos',
    show: !!rolesNode
  }];
  const visibles = tabs.filter(t => t.show);
  const [tab, setTab] = useState<Tab>('general');
  // Cada vez que se entra/vuelve a la pestaña "General" se limpia el
  // formulario de contraseña: nunca debe quedar texto ni mostrarse en claro
  // de una visita anterior.
  useEffect(() => {
    if (tab !== 'general') return;
    setPassActual('');
    setPassNueva('');
    setPassConfirmar('');
    setShowPassActual(false);
    setShowPassNueva(false);
    setPassError('');
    setPassOk(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return <PageShell title="Configuración" sub="Perfil, notificaciones, usuarios, roles y conexión del sistema" data-id="element-362" data-mp="2sldaksk96cwk">
      
      {/* ── Sub-navegación ─────────────────────────────────────────────── */}
      {visibles.length > 1 && <div style={{
      display: 'flex',
      gap: 6,
      padding: 5,
      borderRadius: 14,
      background: T.surfaceSubtle,
      border: `1px solid ${T.border}`,
      width: 'fit-content',
      marginBottom: 20,
      flexWrap: 'wrap'
    }} data-id="element-363" data-mp="32gwyarony5a8">
        
          {visibles.map(t => {
        const active = tab === t.id;
        return <button key={t.id} onClick={() => setTab(t.id)} style={{
          position: 'relative',
          padding: '9px 18px',
          borderRadius: 10,
          border: 'none',
          background: active ? T.accentSoft : 'transparent',
          color: active ? T.accent : T.textMuted,
          fontSize: 13,
          fontWeight: active ? 700 : 600,
          cursor: 'pointer'
        }} data-id="element-364" data-mp="3l991fgn55alf">
              
                {t.label}
              </button>;
      })}
        </div>}

      {/* ── Tab General ──────────────────────────────────────────────────── */}
      {tab === 'general' && <>
          <motion.div initial="hidden" animate="show" variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.06
          }
        }
      }} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))',
        gap: 16
      }} data-id="element-365" data-mp="1lxqe903kzmf">
          
            {/* ── Perfil ───────────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-366" data-mp="3es7madht86sh">
            
              <PanelLabel data-id="element-367" data-mp="183kqs5gpvh70">Perfil</PanelLabel>

              <Field label="Nombre" data-id="element-368" data-mp="1d1dwkxe3y96r">
                <input className="getr-input" style={inputStyle} value={draftPerfil.nombre} onChange={e => setDraftPerfil({
              ...draftPerfil,
              nombre: e.target.value
            })} data-id="element-369" data-mp="1d0bad2rqcux6" />
              
              </Field>
              <Field label="Correo" data-id="element-370" data-mp="3s0oa5z8q27xc">
                <input className="getr-input" style={inputStyle} value={draftPerfil.correo} onChange={e => setDraftPerfil({
              ...draftPerfil,
              correo: e.target.value
            })} data-id="element-371" data-mp="3n6ttz5sbsb30" />
              
              </Field>
              <Field label="Rol" data-id="element-372" data-mp="3daqu66ky4oj8">
                <input className="getr-input" style={{
              ...inputStyle,
              color: T.textMuted
            }} value={draftPerfil.rol} readOnly data-id="element-373" data-mp="3982jrugl39k5" />
              
              </Field>
            </motion.div>

            {/* ── Seguridad / Cambiar contraseña ──────────────────────── */}
            {cambiarPassword && <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-374" data-mp="12gbi4jigte5d">

              <PanelLabel data-id="element-375" data-mp="xjkzppj6u6gs">Seguridad</PanelLabel>

              <Field label="Contraseña actual" data-id="element-376" data-mp="151ss6xnzyog7">
                <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-377" data-mp="15110vz1fncvq">
                  <input className="getr-input" type={showPassActual ? 'text' : 'password'} style={{
                ...inputStyle,
                paddingRight: 38
              }} value={passActual} onChange={e => alEscribirPass(setPassActual)(e.target.value)} autoComplete="off" name="perfil-pass-actual" placeholder="••••••••" data-id="element-378" data-mp="1erm3dxk891jx" />
                  <button type="button" onClick={() => setShowPassActual(v => !v)} aria-label={showPassActual ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
                position: 'absolute',
                right: 8,
                background: 'transparent',
                border: 'none',
                color: T.textMuted,
                display: 'flex',
                cursor: 'pointer',
                padding: 4
              }} data-id="element-379" data-mp="r32rz95vs8nm">

                    {showPassActual ? <EyeOffIcon size={16} data-id="element-380" data-mp="2qrbuiefii5gt" /> : <EyeIcon size={16} data-id="element-381" data-mp="2qq0pyce8ston" />}
                  </button>
                </div>
              </Field>

              <Field label="Nueva contraseña" data-id="element-382" data-mp="2urcf6gt7fcwb">
                <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-383" data-mp="2zjdmrpffgfg0">
                  <input className="getr-input" type={showPassNueva ? 'text' : 'password'} style={{
                ...inputStyle,
                paddingRight: 38
              }} value={passNueva} onChange={e => alEscribirPass(setPassNueva)(e.target.value)} autoComplete="new-password" name="perfil-pass-nueva" placeholder={`Mínimo ${MIN_PASS} caracteres`} data-id="element-384" data-mp="34h5i6tfa6joy" />
                  <button type="button" onClick={() => setShowPassNueva(v => !v)} aria-label={showPassNueva ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
                position: 'absolute',
                right: 8,
                background: 'transparent',
                border: 'none',
                color: T.textMuted,
                display: 'flex',
                cursor: 'pointer',
                padding: 4
              }} data-id="element-385" data-mp="2lrpb1n93bszk">

                    {showPassNueva ? <EyeOffIcon size={16} data-id="element-386" data-mp="1gn1yr6odu19f" /> : <EyeIcon size={16} data-id="element-387" data-mp="1btpok1kg9x07" />}
                  </button>
                </div>
              </Field>

              {/* La confirmación sigue al mismo ojito que la nueva: se comparan
               entre sí, así que verlas por separado no tendría sentido. */}
              <Field label="Confirmar nueva contraseña" data-id="element-388" data-mp="12r80dhl8iuak">
                <input className="getr-input" type={showPassNueva ? 'text' : 'password'} style={inputStyle} value={passConfirmar} onChange={e => alEscribirPass(setPassConfirmar)(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') void handleCambiarPassword();
            }} autoComplete="new-password" name="perfil-pass-confirmar" placeholder="Repite la nueva contraseña" data-id="element-389" data-mp="xt6fcr490kwg" />

              </Field>

              {passError && <div style={{
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            borderRadius: 10,
            padding: '9px 12px',
            fontSize: 12.5,
            lineHeight: 1.45,
            marginBottom: 10
          }} data-id="element-390" data-mp="2iaupytn0vm96">

                  {passError}
                </div>}
              {passOk && <div style={{
            background: T.okSoft,
            border: `1px solid ${T.ok}33`,
            color: T.ok,
            borderRadius: 10,
            padding: '9px 12px',
            fontSize: 12.5,
            lineHeight: 1.45,
            fontWeight: 600,
            marginBottom: 10
          }} data-id="element-391" data-mp="bmkk1820wegh">

                  Contraseña actualizada. Úsala la próxima vez que entres.
                </div>}

              <button type="button" onClick={() => void handleCambiarPassword()} disabled={cambiandoPass || !passActual || !passNueva || !passConfirmar} style={{
            ...primaryButton,
            width: '100%',
            justifyContent: 'center',
            opacity: cambiandoPass || !passActual || !passNueva || !passConfirmar ? 0.5 : 1,
            cursor: cambiandoPass || !passActual || !passNueva || !passConfirmar ? 'not-allowed' : 'pointer'
          }} data-id="element-392" data-mp="uah68abm45xr">

                <LockIcon size={14} data-id="element-393" data-mp="3hfv62dnx2gsb" />
                {cambiandoPass ? 'Actualizando…' : 'Cambiar contraseña'}
              </button>

              <div style={{
            marginTop: 10,
            fontSize: 11.5,
            color: T.textMuted,
            lineHeight: 1.45
          }} data-id="element-394" data-mp="3cg0cg2crbd3k">

                Se pide la actual a propósito: si alguien se topa con tu sesión
                abierta, que no pueda cambiarla sin saberla.
              </div>
            </motion.div>}


            {/* ── Notificaciones ───────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-395" data-mp="261retf8pr6qy">
            
              <PanelLabel data-id="element-396" data-mp="211vafew3tdav">Notificaciones</PanelLabel>
              <Toggle label="Alertas fuera de rango" checked={draftNotif.fueraRango} onChange={() => setDraftNotif({
            ...draftNotif,
            fueraRango: !draftNotif.fueraRango
          })} data-id="element-397" data-mp="2113kokp6u9si" />
            
              <Toggle label="Resumen diario" checked={draftNotif.resumen} onChange={() => setDraftNotif({
            ...draftNotif,
            resumen: !draftNotif.resumen
          })} data-id="element-398" data-mp="1dbpmliwz6x0s" />
            
              <Toggle label="Cambios de auditoría" checked={draftNotif.auditoria} onChange={() => setDraftNotif({
            ...draftNotif,
            auditoria: !draftNotif.auditoria
          })} data-id="element-399" data-mp="1n2kb6txp28pe" />
            
              <Toggle label="Enviar por correo" checked={draftNotif.email} onChange={() => setDraftNotif({
            ...draftNotif,
            email: !draftNotif.email
          })} data-id="element-400" data-mp="1w3s7j6gngzgu" />
            
            </motion.div>

            {/* ── Preferencias ─────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-401" data-mp="6ou37c3gf0ah">
            
              <PanelLabel data-id="element-402" data-mp="3vaywepgqbqfz">Preferencias</PanelLabel>
              <Field label="Lux mínimo global" data-id="element-403" data-mp="3qiflk09ff9ev">
                <input className="getr-input" style={inputStyle} type="number" value={draftPrefs.luxMin} onChange={e => setDraftPrefs({
              ...draftPrefs,
              luxMin: Number(e.target.value)
            })} data-id="element-404" data-mp="3qhmgd3q48ryu" />
              
              </Field>
              <Field label="Idioma" data-id="element-405" data-mp="i22w2u7fajvx">
                <select className="getr-input" style={inputStyle} value={draftPrefs.idioma} onChange={e => setDraftPrefs({
              ...draftPrefs,
              idioma: e.target.value
            })} data-id="element-406" data-mp="i11hifr77f9g">
                
                  <option data-id="element-407" data-mp="43cub19nrw18">Español</option>
                  <option data-id="element-408" data-mp="42l09uvpm6d2">English</option>
                </select>
              </Field>
              <Field label="Tema" data-id="element-409" data-mp="2svfdby9gn7ol">
                <select className="getr-input" style={inputStyle} value={draftPrefs.tema} onChange={e => setDraftPrefs({
              ...draftPrefs,
              tema: e.target.value
            })} data-id="element-410" data-mp="2swi6kcu99m7o">
                
                  <option data-id="element-411" data-mp="2q8wn51822kp6">Oscuro</option>
                  <option data-id="element-412" data-mp="2va3qmo360xq1">Claro</option>
                  <option data-id="element-413" data-mp="2v7re4qg6ffkd">Automático</option>
                </select>
              </Field>
            </motion.div>
          </motion.div>

          {/* ── Botón Guardar ─────────────────────────────────────────── */}
          <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} style={{
        marginTop: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 14
      }} data-id="element-414" data-mp="2ldhaudpp4wuv">
          
            <button onClick={handleSave} disabled={saving || !hasChanges} style={{
          ...primaryButton,
          opacity: !hasChanges || saving ? 0.5 : 1,
          cursor: !hasChanges || saving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transition: 'opacity 0.2s'
        }} data-id="element-415" data-mp="1xk8tah98qn00">
            
              {saving ? <>
                  <span style={{
              width: 14,
              height: 14,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#ffffff',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.7s linear infinite'
            }} data-id="element-416" data-mp="1a0jb8pelp5y3" />
              
                  Guardando…
                </> : <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-417" data-mp="1sv7s1fbszzgj">
                
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" data-id="element-418" data-mp="21q4ayzlbsqhl" />
                    <polyline points="17 21 17 13 7 13 7 21" data-id="element-419" data-mp="1xnr8ql5sfapk" />
                    <polyline points="7 3 7 8 15 8" data-id="element-420" data-mp="1xqn4q3y2rpbd" />
                  </svg>
                  Guardar cambios
                </>}
            </button>

            {/* Indicador de cambios pendientes */}
            {hasChanges && !saved && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} style={{
          fontSize: 12,
          color: T.textMuted
        }} data-id="element-421" data-mp="3e1of8mww624i">
            
                Cambios sin guardar
              </motion.span>}

            {/* Confirmación de guardado */}
            {saved && <motion.span initial={{
          opacity: 0,
          x: -6
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0
        }} style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.ok,
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-422" data-mp="3s1foybgdkwcp">
            
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-423" data-mp="3921tfp2zzdk3">
              
                  <polyline points="20 6 9 17 4 12" data-id="element-424" data-mp="2blwfdsqdptwn" />
                </svg>
                ¡Guardado correctamente!
              </motion.span>}
          </motion.div>

          {/* Keyframe para el spinner */}
          <style data-id="element-425" data-mp="2916sq24ajb10">{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>}

      {/* ── Tab Usuarios ─────────────────────────────────────────────────── */}
      {tab === 'usuarios' && usuariosNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-426" data-mp="1z2r20096e3s1">
        
          {usuariosNode}
        </motion.div>}

      {/* ── Tab Roles ────────────────────────────────────────────────────── */}
      {tab === 'roles' && rolesNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-427" data-mp="1lb2n5p7jl17d">
        
          {rolesNode}
        </motion.div>}
    </PageShell>;
}